/** Stage 1 — SAP backend codes → Domain names (used internally for Area routing only). */
const SAP_CODE_TO_DOMAIN_MAP: Record<string, string> = {
  CCUS:                  'CCUS',
  CORPORATEANALYTICS:    'Data Management',
  DATAANDAI:             'Data and Analytics',
  DEVELOPMENT:           'Planning and Economics',
  DRILLING:              'Drilling',
  ECONOMICS:             'Planning and Economics',
  ENERGYTRANSITION:      'Energy Transition',
  EXPLORATION:           'Exploration',
  FACILITIES:            'Process Engineering',
  FIELDPLANNING:         'Planning and Economics',
  FLOWASSURANCE:         'Flow Assurance',
  GEOLOGYMODELLING:      'Geoscience',
  GEOPHYSICS:            'Geoscience',
  HSE:                   'Sustainability',
  INFORMATIONMANAGEMENT: 'Data Management',
  PA:                    'Production Engineering',
  PRODUCTION:            'Production Engineering',
  REALTIMEOPERATIONS:    'Production Operations',
  RESERVOIRENGINEERING:  'Reservoir Engineering',
  RESERVOIRGEOMECHANICS: 'Geomechanics',
  SUBSURFACE:            'Subsurface',
  SUSTAINABILITY:        'Sustainability',
  WELLENGINEERING:       'Drilling Engineering',
};

const DOMAIN_DETAIL_LABEL_MAP: Record<string, string> = {
  CCUS:                  'CCUS',
  CORPORATEANALYTICS:    'Corporate Analytics',
  DATAANDAI:             'Data and AI',
  DEVELOPMENT:           'Development',
  DRILLING:              'Drilling',
  ECONOMICS:             'Economics',
  ENERGYTRANSITION:      'Energy Transition',
  EXPLORATION:           'Exploration',
  FACILITIES:            'Facilities',
  FIELDPLANNING:         'Field Development Planning',
  FLOWASSURANCE:         'Flow Assurance',
  GEOLOGYMODELLING:      'Geology Modelling',
  GEOPHYSICS:            'Geophysics',
  HSE:                   'HSE',
  INFORMATIONMANAGEMENT: 'Data Engineering',
  PA:                    'PA',
  PRODUCTION:            'Production',
  REALTIMEOPERATIONS:    'Real-Time Operations',
  RESERVOIRENGINEERING:  'Reservoir Engineering',
  RESERVOIRGEOMECHANICS: 'Reservoir Geomechanics',
  SUBSURFACE:            'Subsurface',
  SUSTAINABILITY:        'Sustainability',
  WELLENGINEERING:       'Well Engineering',
};

/** Stage 2 — Domain names → Area name(s). Non-SAP products resolve here directly. */
const DOMAIN_TO_AREA_MAP: Record<string, string | string[]> = {
  'AI':                            'AI',
  'CCUS':                          ['Subsurface', 'Sustainability'],
  'Data':                          'Data Management',
  'Data and Analytics':            'Data Management',
  'Data Management':               'Data Management',
  'Drilling':                      'Well Construction',
  'Drilling Engineering':          'Well Construction',
  'Drilling Operations':           'Well Construction',
  'Energy Transition':             'Sustainability',
  'Exploration':                   'Subsurface',
  'Flow Assurance':                'Production',
  'Geology':                       'Subsurface',
  'Geology Modelling':             'Subsurface',
  'Geomechanics':                  'Subsurface',
  'Geophysics':                    'Subsurface',
  'Geoscience':                    'Subsurface',
  'Geothermal':                    ['Subsurface', 'Sustainability'],
  'Geothermal and Mining':         'Subsurface',
  'Petrophysics':                  'Subsurface',
  'Planning and Economics':        'Subsurface',
  'Process Engineering':           'Production',
  'Production Engineering':        'Production',
  'Production Operations':         'Production',
  'Reservoir Engineering':         'Subsurface',
  'Reservoir Geomechanics':        'Subsurface',
  'Seismic Imaging and Processing': 'Subsurface',
  'Subsea':                        'Well Construction',
  'Subsurface':                    'Subsurface',
  'Sustainability':                'Sustainability',
};

function toDomain(code: string): string {
  return SAP_CODE_TO_DOMAIN_MAP[code.toUpperCase()] ?? code;
}

function toAreas(domain: string): string[] {
  const result = DOMAIN_TO_AREA_MAP[domain];
  if (!result) return [];
  return Array.isArray(result) ? result : [result];
}

const CHALLENGE_LABEL_MAP: Record<string, string> = {
  ALARMFATIGUE:                             'Alarm Fatigue',
  ARTIFICIALLIFTRELIABILITY:                'Artificial Lift Reliability',
  ASSETSCREENINGANDPORTFOLIOEVALUATION:     'Asset Screening & Portfolio Evaluation',
  BROWNFIELDDEVELOPMENT:                    'Brownfield Development',
  BROWNFIELDOPTIMIZATION:                   'Brownfield Optimization',
  CAPEXOPTIMIZATION:                        'CAPEX Optimization',
  CARBONCAPTUREANDSTORAGE:                  'Carbon Capture & Storage',
  CARBONATEANDFRACTURES:                    'Carbonate & Fractures',
  CARBONINTENSITY:                          'Carbon Intensity',
  COMPLEXANDTIMECONSUMINGHISTORYMATCHINGPROCESS: 'Complex & Time-Consuming History Matching Process',
  CORPORATEGHGINVENTORYANALYSIS:            'Corporate GHG Inventory Analysis',
  COSTESTIMATION:                           'Cost Estimation',
  COSTUNCERTAINTY:                          'Cost Uncertainty',
  DATAFRAGMENTATION:                        'Data Fragmentation',
  DATAINCONSISTENCY:                        'Data Inconsistency',
  DECARBONIZATIONPLANNING:                  'Decarbonization Planning',
  DECISIONCONFIDENCE:                       'Decision Confidence',
  DECISIONDELAY:                            'Decision Delay',
  DECISIONMAKINGCOMPLEXITY:                 'Decision Making Complexity',
  DECISIONMAKINGFROMENSEMBLES:              'Decision Making from Ensembles',
  DECISIONSPEED:                            'Decision Speed',
  DECISIONSUPPORT:                          'Decision Support',
  DECLINECURVELIMITATIONS:                  'Decline Curve Limitations',
  DEEPWATEREXPLORATIONANDPRODUCTION:        'Deep Water Exploration & Production',
  DESIGNDEPENDENCIES:                       'Design Dependencies',
  DESIGNREFINEMENT:                         'Design Refinement',
  DOMAININFORMATIONEXTRACTION:              'Domain Information Extraction',
  DRILLINGFEASIBILITY:                      'Drilling Feasibility',
  DRILLINGNONPRODUCTIVETIME:                'Drilling Non-Productive Time',
  DRILLINGPERFORMANCEUNCERTAINTY:           'Drilling Performance Uncertainty',
  DRILLINGPLANNINGINEFFICIENCY:             'Drilling Planning Inefficiency',
  DYNAMICDRILLINGOPERATIONS:                'Dynamic Drilling Operations',
  DYNAMICOPERATIONS:                        'Dynamic Operations',
  DYNAMICRESERVOIRMODELCOMPLEXITY:          'Dynamic Reservoir Model Complexity',
  DYNAMICRESERVOIRMODELQCCOMPLEXITY:        'Dynamic Reservoir Model QC Complexity',
  ECONOMICASSESSMENT:                       'Economic Assessment',
  ECONOMICEVALUATION:                       'Economic Evaluation',
  ECONOMICMODELINCONSISTENCY:               'Economic Model Inconsistency',
  EMISSIONSDATAAGGREGATION:                 'Emissions Data Aggregation',
  EMISSIONSQUANTIFICATION:                  'Emissions Quantification',
  ENERGYCONSUMPTION:                        'Energy Consumption',
  ENERGYTRANSITION:                         'Energy Transition',
  ENHANCEDOILRECOVERY:                      'Enhanced Oil Recovery',
  EQUIPMENTFAILURE:                         'Equipment Failure',
  EQUIPMENTFAILUREPREDICTION:               'Equipment Failure Prediction',
  ESPFUNCTIONALFAILURE:                     'ESP Functional Failure',
  ESPUNDERPERFORMANCE:                      'ESP Underperformance',
  EXPLORATIONRISKASSESSMENT:                'Exploration Risk Assessment',
  FIELDDEVELOPMENTPLANNING:                 'Field Development Planning',
  FIELDDEVELOPMENTPLANNINGUNCERTAINTY:      'Field Development Planning Uncertainty',
  FLOWASSURANCEINTEGRATION:                 'Flow Assurance Integration',
  FLOWASSURANCERISK:                        'Flow Assurance Risk',
  FORECASTINCONSISTENCY:                    'Forecast Inconsistency',
  FRAGMENTEDINSPECTIONDATA:                 'Fragmented Inspection Data',
  HYDROCARBONEFFECTSONLOGS:                 'Hydrocarbon Effects on Logs',
  INACCESSIBLEDATA:                         'Inaccessible Data',
  INFRASTRUCTURECONSTRAINTS:                'Infrastructure Constraints',
  INJECTIONALLOCATIONOPTIMIZATION:          'Injection Allocation Optimization',
  INTEGRATEDWORKFLOWS:                      'Integrated Workflows',
  INTERPRETATIONVARIABILITY:                'Interpretation Variability',
  INTERVENTIONSCREENINGSCALABILITY:         'Intervention Screening Scalability',
  INTERWELLCORRELATIONCOMPLEXITY:           'Interwell Correlation Complexity',
  INVESTMENTDECISION:                       'Investment Decision',
  LACKOFAUTOMATION:                         'Lack of Automation',
  LOGDATAQUALITYISSUES:                     'Log Data Quality Issues',
  LOGNORMALIZATIONINCONSISTENCY:            'Log Normalization Inconsistency',
  LONGMODELCREATIONCYCLES:                  'Long Model Creation Cycles',
  MANUALCALIBRATION:                        'Manual Calibration',
  MANUALDATAHANDLING:                       'Manual Data Handling',
  MANUALDECLINECURVEANALYSIS:               'Manual Decline Curve Analysis',
  MANUALINTERPRETATION:                     'Manual Interpretation',
  MANUALLOGEDITING:                         'Manual Log Editing',
  MANUALPETROPHYSICALPREPROCESSING:         'Manual Petrophysical Preprocessing',
  MANUALREPORTREVIEW:                       'Manual Report Review',
  MANUALWORKFLOWS:                          'Manual Workflows',
  MISSINGLOGINTERVALS:                      'Missing Log Intervals',
  MULTIPHASESIMULATIONCOMPLEXITY:           'Multiphase Simulation Complexity',
  NONLINEARFLOWBEHAVIOR:                    'Nonlinear Flow Behavior',
  NONPRODUCTIVETIME:                        'Non-Productive Time',
  OFFSETWELLDATAFRAGMENTATION:              'Offset Well Data Fragmentation',
  OPERATIONALCONSTRAINTS:                   'Operational Constraints',
  OPERATIONALDECISIONUNCERTAINTY:           'Operational Decision Uncertainty',
  OPERATIONALSAFETY:                        'Operational Safety',
  PIPELINEINTEGRITYRISK:                    'Pipeline Integrity Risk',
  PLUGANDABANDONMENTCOMPLEXITY:             'Plug & Abandonment Complexity',
  PORTFOLIOPRIORITIZATION:                  'Portfolio Prioritization',
  PORTFOLIOSCREENINGSCALABILITY:            'Portfolio Screening Scalability',
  PROCESSMODELINGCOMPLEXITY:                'Process Modeling Complexity',
  PRODUCTIONENGINEERINGWORKFLOWS:           'Production Engineering Workflows',
  PRODUCTIONFORECASTACCURACY:               'Production Forecast Accuracy',
  PRODUCTIONFORECASTINGSCALABILITY:         'Production Forecasting Scalability',
  PRODUCTIONFORECASTINGVARIABILITY:         'Production Forecasting Variability',
  PRODUCTIONFORECASTUNCERTAINTY:            'Production Forecast Uncertainty',
  PRODUCTIONMAXIMIZATION:                   'Production Maximization',
  PRODUCTIONMODELINGINEFFICIENCY:           'Production Modeling Inefficiency',
  PRODUCTIONOPTIMIZATION:                   'Production Optimization',
  PROSPECTSCREENINGINEFFICIENCY:            'Prospect Screening Inefficiency',
  REACTIVEHSEMONITORING:                    'Reactive HSE Monitoring',
  REACTIVEINTERVENTIONPLANNING:             'Reactive Intervention Planning',
  REACTIVEMAINTENANCE:                      'Reactive Maintenance',
  REALTIMEOPERATIONALRISKASSESSMENT:        'Real-Time Operational Risk Assessment',
  REALTIMEPRODUCTIONSURVEILLANCE:           'Real-Time Production Surveillance',
  REGULATORYCOMPLEXITY:                     'Regulatory Complexity',
  REGULATORYCOMPLIANCE:                     'Regulatory Compliance',
  REGULATORYREPORTING:                      'Regulatory Reporting',
  RESERVOIRCOMPLEXITY:                      'Reservoir Complexity',
  RESERVOIRCONNECTIVITYUNCERTAINTY:         'Reservoir Connectivity Uncertainty',
  RESERVOIRMODELINGCOMPLEXITY:              'Reservoir Modeling Complexity',
  RESERVOIRMODELUPDATE:                     'Reservoir Model Update',
  RESERVOIRSIMULATIONCOMPLEXITYANDUNCERTAINTY: 'Reservoir Simulation Complexity & Uncertainty',
  RESERVOIRSIMULATIONENSEMBLECOMPLEXITY:    'Reservoir Simulation Ensemble Complexity',
  RESERVOIRUNCERTAINTYMANAGEMENT:           'Reservoir Uncertainty Management',
  RESOURCEALLOCATION:                       'Resource Allocation',
  RESOURCECONSTRAINTOPTIMIZATION:           'Resource Constraint Optimization',
  RESOURCEOPTIMIZATION:                     'Resource Optimization',
  RIGSCHEDULINGCOMPLEXITY:                  'Rig Scheduling Complexity',
  RISKREDUCTION:                            'Risk Reduction',
  ROPVARIABILITY:                           'ROP Variability',
  ROTATINGEQUIPMENTRELIABILITY:             'Rotating Equipment Reliability',
  RUNTIMELOSS:                              'Runtime Loss',
  SCENARIOCOMPARISON:                       'Scenario Comparison',
  SCENARIOEVALUATION:                       'Scenario Evaluation',
  SCENARIOEVALUATIONLIMITATIONS:            'Scenario Evaluation Limitations',
  SCHEDULINGCOMPLEXITY:                     'Scheduling Complexity',
  SEISMICDATAATSCALE:                       'Seismic Data at Scale',
  SEISMICMONITORINGCOMPLEXITY:              'Seismic Monitoring Complexity',
  SLOWINVESTMENTDECISIONMAKING:             'Slow Investment Decision Making',
  STRANDEDGASASSETTIEBACKANDDEVELOPMENT:    'Stranded Gas Asset Tieback & Development',
  STRATIGRAPHICMARKERPICKING:               'Stratigraphic Marker Picking',
  STUCKPIPERISK:                            'Stuck Pipe Risk',
  SUBOPTIMALDRILLINGPARAMETERS:             'Suboptimal Drilling Parameters',
  SUBSURFACEUNCERTAINTIES:                  'Subsurface Uncertainties',
  SURFACECONSTRAINTS:                       'Surface Constraints',
  TIGHTGASPRODUCTIONFORECASTING:            'Tight Gas Production Forecasting',
  UNCERTAINTYANALYSIS:                      'Uncertainty Analysis',
  UNCERTAINTYMANAGEMENT:                    'Uncertainty Management',
  UNCONVCOALBEDMETHANE:                     'Unconventional Coalbed Methane',
  UNCONVENTIONALDEVELOPMENT:                'Unconventional Development',
  UNCONVHEAVYOIL:                           'Unconventional Heavy Oil',
  UNCONVSHALE:                              'Unconventional Shale',
  UNCONVTIGHTGASANDOIL:                     'Unconventional Tight Gas & Oil',
  UNPLANNEDDOWNTIME:                        'Unplanned Downtime',
  UNSTRUCTUREDDATAANALYSIS:                 'Unstructured Data Analysis',
  UNSTRUCTUREDLEGACYDATA:                   'Unstructured Legacy Data',
  WATERFLOODINEFFICIENCY:                   'Waterflood Inefficiency',
  WELLDESIGNCONSISTENCY:                    'Well Design Consistency',
  WELLINTEGRITYASSESSMENT:                  'Well Integrity Assessment',
  WELLUNDERPERFORMANCE:                     'Well Underperformance',
  WORKFLOWDISCONNECT:                       'Workflow Disconnect',
  WORKFLOWFRAGMENTATION:                     'Workflow Fragmentation',
  WORKFLOWINTEGRATION:                       'Workflow Integration',
};

const PLATFORM_LABEL_MAP: Record<string, string> = {
  aiworkspace:                   'Lumi AI Workspace',
  drillops:                      'DrillOps',
  drillplan:                     'DrillPlan',
  exploreplan:                   'ExplorePlan',
  fdplan:                        'FDPlan',
  gaia:                          'GAIA',
  lumi:                          'Lumi',
  'lumi-ai-workspace':           'Lumi AI Workspace',
  'lumi-tools':                  'Lumi Tools',
  petrel:                        'Petrel',
  prodops:                       'ProdOps',
  studiomanager:                 'Studio',
  'sustainability-platform-app': 'Sustainability Platform App',
  techlog:                       'Techlog',
  tela:                          'Tela',
};

/** Returns the Area label for the first recognisable domain code in the array. */
export function getDomainLabel(domains: string[] | undefined): string {
  if (!domains?.length) return '';
  for (const d of domains) {
    const areas = toAreas(toDomain(d));
    if (areas.length) return areas[0];
  }
  return '';
}

/** Returns a deduplicated array of Area labels for all domain codes (used for card/header display). */
export function getDomainLabels(domains: string[] | undefined): string[] {
  if (!domains?.length) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const d of domains) {
    for (const area of toAreas(toDomain(d))) {
      if (!seen.has(area)) {
        seen.add(area);
        result.push(area);
      }
    }
  }
  return result;
}

/** Returns per-code human-readable display labels for all domain codes (used for Domain detail checklist). */
export function getDomainDetailLabels(domains: string[] | undefined): string[] {
  if (!domains?.length) return [];
  return domains.map(d => DOMAIN_DETAIL_LABEL_MAP[d.toUpperCase()] ?? d);
}

/** Returns the display label for a challenge code, falling back to the raw code. */
export function getChallengeLabel(code: string): string {
  return CHALLENGE_LABEL_MAP[code] ?? code;
}

const LIFECYCLE_CONVENTIONAL_LABEL_MAP: Record<string, string> = {
  ABANDONMENT:      'Abandonment',
  APPRAISAL:        'Appraisal',
  DECOMMISSIONING:  'Decommissioning',
  DEVELOPMENT:      'Development',
  EXPLORATION:      'Exploration',
  PRODUCTION:       'Production',
};

/** Returns the display label for a lifecycle conventional code, falling back to title-cased raw value. */
export function getLifecycleConventionalLabel(code: string): string {
  return LIFECYCLE_CONVENTIONAL_LABEL_MAP[code]
    ?? code.charAt(0).toUpperCase() + code.slice(1).toLowerCase();
}

/** Primary Tela listing type from catalog `categories[].code` (agent > skill > tool > foundation model if multiple). */
export function deriveTelaListingKindFromCategories(
  categories: { code: string }[] | undefined,
): 'Tool' | 'Skill' | 'Agent' | 'Foundation Model' | null {
  if (!categories?.length) return null;
  const codes = new Set(categories.map(c => c.code));
  if (codes.has('tela-agent')) return 'Agent';
  if (codes.has('tela-skill')) return 'Skill';
  if (codes.has('tela-tool')) return 'Tool';
  if (codes.has('tela-foundation-model') || codes.has('foundation-model')) return 'Foundation Model';
  return null;
}

/** Returns the display label for a raw platformType string (e.g. "petrel" → "Petrel"). */
export function getPlatformLabel(platformType: string | undefined): string {
  if (!platformType) return '';
  return PLATFORM_LABEL_MAP[platformType.toLowerCase()] ?? (
    platformType.charAt(0).toUpperCase() + platformType.slice(1).toLowerCase()
  );
}

/**
 * Resolves the display label for a product's platform, preferring additionalPlatformTypes over platformType.
 * Multiple platform types are joined with " · " (e.g. "Tela · Lumi AI Workspace").
 */
export function resolvePlatformLabel(additionalPlatformTypes?: string[], platformType?: string): string {
  if (additionalPlatformTypes?.length) {
    return additionalPlatformTypes.map(pt => getPlatformLabel(pt)).join(' · ');
  }
  return getPlatformLabel(platformType);
}

const ICONS = 'assets/icons/platform-icons';

/**
 * Explicit icon paths keyed by compatibleProduct label.
 * Petrel and Techlog have hosting-variant icons handled separately below.
 */
/** Icons with no theme variant (same in light and dark). */
const SPECIFIC_PLATFORM_ICONS: Record<string, string> = {
  'Studio': `${ICONS}/studio_icon.svg`,
  'Tela':   'assets/images/logos/SLB/tela.svg',
};

/** Products that have hosting-specific icon variants (On-Prem / Cloud). */
const HOSTING_VARIANT_PRODUCTS = new Set(['Petrel', 'Techlog']);

/** Petrel / Techlog icon filenames keyed by "<product>-<suffix>". */
const HOSTING_VARIANT_ICONS: Record<string, string> = {
  'Petrel-On-Prem':  `${ICONS}/petrel_on_prem_icon.svg`,
  'Petrel-XD':       `${ICONS}/petrel_XD_icon.svg`,
  'Techlog-On-Prem': `${ICONS}/techlog_on_prem_icon.svg`,
  'Techlog-XD':      `${ICONS}/techlog_XD_icon.svg`,
};

/** Delfi-family products (DrillPlan, DrillOps, …) share the Delfi icon. */
export const DELFI_FAMILY = new Set(['DrillPlan', 'DrillOps', 'ExplorePlan', 'FDPlan', 'GAIA', 'ProdOps']);

/** Lumi-family products share the Lumi icon. */
export const LUMI_FAMILY = new Set(['Lumi', 'Lumi AI Workspace', 'Lumi Tools']);

/** Light-mode icons: blue background, white logo — pop against a light/white surface. */
const ICON_DELFI_LIGHT = `${ICONS}/delfi_icon_blue.svg`;
const ICON_LUMI_LIGHT  = `${ICONS}/lumi_Icon_blue.png`;

/** Dark-mode icons: white background, blue logo — pop against a dark surface. */
const ICON_DELFI_DARK  = `${ICONS}/delfi_icon_white.svg`;
const ICON_LUMI_DARK   = `${ICONS}/lumi_Icon_white.png`;

const ICON_PLACEHOLDER = `${ICONS}/placeholder.svg`;

const HOSTING_DISPLAY_MAP: Record<string, 'Cloud / SaaS' | 'On-Prem'> = {
  CLOUD: 'Cloud / SaaS',
  CLOUD_SAAS: 'Cloud / SaaS',
  ON_PREM: 'On-Prem',
  SAAS: 'Cloud / SaaS',
};

export function mapHostingTypes(raw: string[] | undefined): ('Cloud / SaaS' | 'On-Prem')[] {
  if (!raw?.length) return [];
  const seen = new Set<string>();
  const result: ('Cloud / SaaS' | 'On-Prem')[] = [];
  for (const v of raw) {
    const mapped = HOSTING_DISPLAY_MAP[v] ?? 'On-Prem';
    if (!seen.has(mapped)) { seen.add(mapped); result.push(mapped); }
  }
  return result;
}

function getHostingVariantIcon(compatibleProduct: string, hostingTypes: string[]): string {
  if (hostingTypes.length !== 1) return ICON_PLACEHOLDER;
  const suffix = hostingTypes[0] === 'On-Prem' ? 'On-Prem' : hostingTypes[0] === 'Cloud / SaaS' ? 'XD' : null;
  if (suffix) return HOSTING_VARIANT_ICONS[`${compatibleProduct}-${suffix}`] ?? ICON_PLACEHOLDER;
  return ICON_PLACEHOLDER;
}

/**
 * Returns the asset path for the platform icon for a given compatibleProduct + hostingTypes.
 * Pass isDark=true when rendering on a dark background to get the blue-background variant.
 *
 * Resolution order:
 *  1. Specific icon registered in SPECIFIC_PLATFORM_ICONS (no theme variant)
 *  2. Hosting-variant icon for Petrel / Techlog
 *  3. Lumi icon (theme-aware) for Lumi-family products
 *  4. Delfi icon (theme-aware) for Delfi-family products
 *  5. Placeholder for any other non-empty compatibleProduct
 *  6. null if compatibleProduct is empty
 */
export function getPlatformIconPath(compatibleProduct: string, hostingTypes: string[], isDark = false): string | null {
  if (!compatibleProduct) return null;

  if (SPECIFIC_PLATFORM_ICONS[compatibleProduct]) {
    return SPECIFIC_PLATFORM_ICONS[compatibleProduct];
  }

  if (HOSTING_VARIANT_PRODUCTS.has(compatibleProduct)) {
    return getHostingVariantIcon(compatibleProduct, hostingTypes);
  }

  if (LUMI_FAMILY.has(compatibleProduct)) return isDark ? ICON_LUMI_DARK : ICON_LUMI_LIGHT;

  if (DELFI_FAMILY.has(compatibleProduct)) return isDark ? ICON_DELFI_DARK : ICON_DELFI_LIGHT;

  return ICON_PLACEHOLDER;
}

/** Derives the display badge for a product from its tag list. Priority: Spotlight > New > Coming Soon > null. */
export function deriveBadge(productTags: string[]): 'Spotlight' | 'New' | 'Coming Soon' | null {
  if (productTags.includes('spotlight')) return 'Spotlight';
  if (productTags.includes('new')) return 'New';
  if (productTags.includes('coming-soon')) return 'Coming Soon';
  return null;
}
